import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  fetchSession,
  signOut as apiSignOut,
  type QuotaState,
  type SessionUser,
} from "../engine/useRun";

/**
 * Session + quota context.
 *
 * Anonymous users get a free daily allowance (server-enforced). We never wall
 * the first run behind signup — the auth prompt only appears when the server
 * says the quota is spent. This context just holds what the server told us and
 * lets any screen open the auth flow.
 */

interface SessionContextValue {
  user: SessionUser | null;
  quota: QuotaState | null;
  loading: boolean;
  authOpen: boolean;
  /** Optional reason shown atop the auth sheet (e.g. quota exhausted). */
  authReason: string | null;
  openAuth: (reason?: string) => void;
  closeAuth: () => void;
  setSession: (s: { user: SessionUser | null; quota: QuotaState | null }) => void;
  /** Apply a fresher quota snapshot returned by /api/run. */
  updateQuota: (q: QuotaState | null | undefined) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await fetchSession();
      setUser(s.user);
      setQuota(s.quota);
    } catch {
      // Offline or no backend yet: treat as a fresh anonymous session so the
      // app still renders and the demo path works.
      setUser(null);
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openAuth = useCallback((reason?: string) => {
    setAuthReason(reason ?? null);
    setAuthOpen(true);
  }, []);
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  const setSession = useCallback(
    (s: { user: SessionUser | null; quota: QuotaState | null }) => {
      setUser(s.user);
      setQuota(s.quota);
    },
    [],
  );

  const updateQuota = useCallback((q: QuotaState | null | undefined) => {
    if (q) setQuota(q);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiSignOut();
    } catch {
      /* ignore — clear locally regardless */
    }
    setUser(null);
    await refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({
      user,
      quota,
      loading,
      authOpen,
      authReason,
      openAuth,
      closeAuth,
      setSession,
      updateQuota,
      refresh,
      signOut,
    }),
    [
      user,
      quota,
      loading,
      authOpen,
      authReason,
      openAuth,
      closeAuth,
      setSession,
      updateQuota,
      refresh,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
